// src/components/BillingTypePopup.js
// ---------------------------------------------------------------------------
// "How should this job be invoiced?"
//
// Shown right after a job is completed (Home, Send Invoices) and from the
// "Completed jobs waiting for an invoice" list on Approved Invoices. Picking
// an option writes the invoice straight into Approved Invoices.
//
//   Standard  - the whole job total, same invoice the old Send Invoices
//               "Approved Invoice" button made. Default for ordinary jobs.
//   Deposit   - a deposit against the job total.
//   Progress  - a progress payment; numbered 1, 2, 3 per contract.
//   Final     - whatever is left on the contract.
//   No Payment Needed (red) - warranty, inspection, proposal, call-back:
//               saves a $0.00 invoice already marked Paid. Pick a reason.
//     Bill Later (blue, inside No Payment Needed, only on a continued job) -
//               the work goes on under the next WO. No invoice is written;
//               the balance rolls over and is billed on that WO.
//
// Jobs that came out of a paid proposal deposit already have a deposit on the
// books, so they open on Progress / Final and do not offer Standard.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import "./BillingTypePopup.css";
import {
  BILLING_KIND,
  BILLING_KIND_LABEL,
  DEPOSIT_PERCENT_OPTIONS,
  buildBillingHeader,
  computeDepositAmount,
  createInvoiceForCompletedJob,
  deferJobBilling,
  formatMoney,
  getBillingContextForJob,
  getContinuedToWO,
  markJobBillLater,
  markJobNoPayment,
  NO_PAYMENT_REASONS,
  parseMoney,
  round2,
} from "../utils/billingFlow";

const PROGRESS_PERCENT_OPTIONS = [
  { value: "10", label: "10%" },
  { value: "20", label: "20%" },
  { value: "25", label: "25%" },
  { value: "30", label: "30%" },
  { value: "33.3333", label: "33⅓%" },
  { value: "40", label: "40%" },
  { value: "50", label: "50%" },
];

const BillingTypePopup = ({ job, onClose, title }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [context, setContext] = useState(null);

  const [kind, setKind] = useState(BILLING_KIND.STANDARD);
  const [jobTotal, setJobTotal] = useState("");
  const [amount, setAmount] = useState("");
  const [percent, setPercent] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [askNoPay, setAskNoPay] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const ctx = await getBillingContextForJob(job);
        if (cancelled) return;

        setContext(ctx);
        setJobTotal(ctx.jobTotal ? String(ctx.jobTotal) : "");

        // Contract jobs: a deposit is already billed, so this is progress or
        // final. Ordinary jobs keep today's behavior by default.
        if (ctx.isContractJob) {
          setKind(BILLING_KIND.PROGRESS);
        } else {
          setKind(BILLING_KIND.STANDARD);
        }
      } catch (err) {
        console.error("Could not load billing info:", err);
        if (!cancelled) setError("Could not load this job's billing history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [job]);

  const total = round2(parseMoney(jobTotal));
  const previouslyBilled = kind === BILLING_KIND.STANDARD ? 0 : context?.previouslyBilled || 0;
  const remainingBefore = round2(total - previouslyBilled);

  // Suggested amount for the chosen kind, until the user types their own.
  useEffect(() => {
    if (!context || amountTouched) return;

    if (kind === BILLING_KIND.STANDARD) {
      setAmount(String(total || 0));
      setPercent("");
    } else if (kind === BILLING_KIND.FINAL) {
      setAmount(remainingBefore > 0 ? String(remainingBefore) : "0");
      setPercent("");
    } else if (kind === BILLING_KIND.DEPOSIT) {
      setPercent("50");
      setAmount(String(computeDepositAmount({ total, mode: "percent", percent: "50" })));
    } else {
      setPercent("");
      setAmount("");
    }
  }, [kind, total, remainingBefore, context, amountTouched]);

  const pickKind = (next) => {
    setKind(next);
    setAmountTouched(false);
  };

  const pickPercent = (value) => {
    setPercent(value);
    setAmountTouched(true);
    if (value) {
      setAmount(String(computeDepositAmount({ total, mode: "percent", percent: value })));
    }
  };

  const due = round2(parseMoney(amount));
  const remainingAfter = round2(remainingBefore - due);
  const sequence = kind === BILLING_KIND.PROGRESS ? (context?.progressCount || 0) + 1 : 0;

  const preview = useMemo(() => {
    if (kind === BILLING_KIND.STANDARD) return "";
    return buildBillingHeader({
      scope: job?.scope,
      kind,
      sequence,
      contractTotal: total,
      previouslyBilled,
      amount: due,
    });
  }, [kind, job, sequence, total, previouslyBilled, due]);

  const warnings = [];
  if (kind !== BILLING_KIND.STANDARD && !total) {
    warnings.push("Enter the job total so the remaining balance can be tracked.");
  }
  // $0 is allowed (warranty / call-back / no-charge work). It is only flagged
  // so nobody sends a $0 invoice by accident - it never blocks Create.
  if (due < 0) {
    warnings.push("The invoice amount cannot be negative.");
  } else if (due === 0) {
    warnings.push(
      kind === BILLING_KIND.FINAL
        ? "Nothing is left to bill on this job - this will create a $0.00 invoice."
        : "This will create a $0.00 invoice. You can edit the amount later in Approved Invoices."
    );
  }
  if (kind !== BILLING_KIND.STANDARD && total && remainingAfter < 0) {
    warnings.push(`This bills ${formatMoney(-remainingAfter)} more than is left on the job.`);
  }
  if (kind === BILLING_KIND.FINAL && context?.hasFinal) {
    warnings.push("A final payment invoice already exists for this job.");
  }
  // Double-billing guard: invoices other WOs on this chain (5456, 5456-2 …)
  // already carry that are not part of the balance shown above.
  const chainExtra = round2((context?.chainBilled || 0) - (kind === BILLING_KIND.STANDARD ? 0 : previouslyBilled));
  if (context && chainExtra > 0.009) {
    const list = (context.chainInvoices || [])
      .slice(0, 4)
      .map((inv) => `${inv.wo || "-"} ${formatMoney(inv.amount ?? inv.rate)}`)
      .join(", ");
    warnings.push(
      `Other WOs on this job were already invoiced ${formatMoney(chainExtra)} (${list}). ` +
        (kind === BILLING_KIND.STANDARD
          ? "A Standard invoice bills the full total again - pick Progress or Final to bill only what is left."
          : "That is not counted in the balance above - check it before sending so nothing is billed twice.")
    );
  }

  const canSave = !loading && !saving && due >= 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const result = await createInvoiceForCompletedJob(job, {
        kind,
        amount: due,
        jobTotal: total,
        origin: "completion-popup",
      });
      onClose?.({ created: !result.existed, existed: result.existed, invoiceId: result.id, kind });
    } catch (err) {
      console.error("Could not create the invoice:", err);
      setError(err?.message || "Could not create the invoice.");
      setSaving(false);
    }
  };

  const noPayment = async (reason) => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await markJobNoPayment(job, { reason });
      alert(`Marked "No Payment Needed" (${reason}).\nA $0.00 invoice was saved as Paid in Approved Invoices.`);
      // existed: true -> pages update the job without a second "invoice created" alert
      onClose?.({ existed: true, noPayment: true, invoiceId: result.id, reason });
    } catch (err) {
      console.error("Could not mark no payment:", err);
      setError(err?.message || "Could not mark this job as No Payment Needed.");
      setSaving(false);
    }
  };

  // The job continues on another WO (Continue was used): its balance rolls
  // over to that WO, which is billed when the work is finished.
  const continuedToWO = getContinuedToWO(job);
  const isContinued = !!(continuedToWO || job?.projectContinuedToJobId);

  const billLater = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await markJobBillLater(job);
      alert(
        `WO ${job?.wo || ""} marked "Bill Later".\n` +
          `No invoice now - the balance rolls over to WO ${result.toWO || continuedToWO} and is billed there.`
      );
      onClose?.({ billLater: true, toWO: result.toWO, toJobId: result.toJobId });
    } catch (err) {
      console.error("Could not mark Bill Later:", err);
      setError(err?.message || "Could not mark this job Bill Later.");
      setSaving(false);
    }
  };

  const decideLater = () => {
    if (saving) return;
    deferJobBilling(job);
    onClose?.({ skipped: true });
  };

  const kinds = context?.isContractJob
    ? [BILLING_KIND.PROGRESS, BILLING_KIND.FINAL, BILLING_KIND.DEPOSIT, BILLING_KIND.STANDARD]
    : [BILLING_KIND.STANDARD, BILLING_KIND.DEPOSIT, BILLING_KIND.PROGRESS, BILLING_KIND.FINAL];

  return (
    <div className="bfp-overlay" onClick={decideLater}>
      <div className="bfp-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="bfp-title" data-build="billlater-oct30">{title || "How should this job be invoiced?"}</h3>
        <p className="bfp-sub">
          {job?.wo ? `WO ${job.wo}` : "No WO"}
          {job?.siteName ? ` · ${job.siteName}` : ""}
          {job?.scope ? ` · ${String(job.scope).replace(/\(\([a-z-]\)\)/gi, "")}` : ""}
        </p>

        {loading ? (
          <p className="bfp-muted">Loading billing history…</p>
        ) : (
          <>
            {context?.isContractJob && (
              <div className="bfp-ledger">
                <div><span>Job total</span><b>{formatMoney(context.jobTotal)}</b></div>
                <div><span>Billed so far</span><b>{formatMoney(context.previouslyBilled)}</b></div>
                <div><span>Remaining</span><b>{formatMoney(context.remaining)}</b></div>
                {context.invoices?.length > 0 && (
                  <ul className="bfp-ledger-list">
                    {context.invoices.map((inv) => (
                      <li key={inv.id}>
                        {inv.wo || "-"} · {BILLING_KIND_LABEL[inv.billingKind] || "Invoice"}
                        {inv.billingKind === BILLING_KIND.PROGRESS && inv.billingSequence
                          ? ` ${inv.billingSequence}`
                          : ""}
                        {" · "}
                        {formatMoney(inv.amount)} · {inv.status || "Draft"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="bfp-kinds" role="radiogroup" aria-label="Invoice type">
              {kinds.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  className={`bfp-kind ${kind === k ? "is-active" : ""}`}
                  onClick={() => pickKind(k)}
                >
                  {k === BILLING_KIND.PROGRESS
                    ? `Progress payment ${sequence || (context?.progressCount || 0) + 1}`
                    : BILLING_KIND_LABEL[k]}
                </button>
              ))}
            </div>

            <div className="bfp-grid">
              <label className="bfp-field">
                <span>Job total</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={jobTotal}
                  onChange={(e) => setJobTotal(e.target.value)}
                  placeholder="0.00"
                  readOnly={!!context?.totalFromJobMoney}
                  title={
                    context?.totalFromJobMoney
                      ? "Includes change orders. Change it from Job Money in the job popup."
                      : undefined
                  }
                />
                {context?.totalFromJobMoney && (
                  <small className="bfp-muted">
                    Includes change orders ({formatMoney(context.changeOrdersTotal)}). Change it in Job Money.
                  </small>
                )}
              </label>

              {(kind === BILLING_KIND.DEPOSIT || kind === BILLING_KIND.PROGRESS) && (
                <label className="bfp-field">
                  <span>Percent of job total</span>
                  <select value={percent} onChange={(e) => pickPercent(e.target.value)}>
                    <option value="">Manual amount</option>
                    {(kind === BILLING_KIND.DEPOSIT ? DEPOSIT_PERCENT_OPTIONS : PROGRESS_PERCENT_OPTIONS).map(
                      (opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      )
                    )}
                  </select>
                </label>
              )}

              <label className="bfp-field">
                <span>
                  {kind === BILLING_KIND.STANDARD
                    ? "Invoice amount"
                    : kind === BILLING_KIND.DEPOSIT
                    ? "Deposit due now"
                    : kind === BILLING_KIND.PROGRESS
                    ? "Progress payment due now"
                    : "Final payment due now"}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setPercent("");
                    setAmountTouched(true);
                  }}
                  placeholder="0.00"
                />
              </label>
            </div>

            {kind !== BILLING_KIND.STANDARD && (
              <div className="bfp-summary">
                {previouslyBilled > 0 && (
                  <div><span>Previously billed</span><b>{formatMoney(previouslyBilled)}</b></div>
                )}
                <div><span>This invoice</span><b>{formatMoney(due)}</b></div>
                <div><span>Remaining balance after</span><b>{formatMoney(remainingAfter)}</b></div>
              </div>
            )}

            {preview && (
              <div className="bfp-preview">
                <div className="bfp-preview-label">Invoice will read</div>
                <pre>{preview}</pre>
              </div>
            )}

            {warnings.length > 0 && (
              <ul className="bfp-warnings">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}

        {error && <p className="bfp-error">{error}</p>}

        <div className="bfp-actions">
          <button type="button" className="bfp-btn bfp-btn-primary" onClick={save} disabled={!canSave}>
            {saving ? "Creating…" : "Create invoice in Approved Invoices"}
          </button>
          <button
            type="button"
            className="bfp-btn"
            onClick={decideLater}
            disabled={saving}
          >
            Decide later
          </button>
          <button
            type="button"
            className="bfp-btn bfp-btn-nopay"
            onClick={() => setAskNoPay((v) => !v)}
            disabled={saving || loading}
            aria-expanded={askNoPay}
            style={{ background: "#dc2626", backgroundImage: "none", borderColor: "#dc2626", color: "#fff" }}
          >
            No Payment Needed
          </button>
        </div>
        {askNoPay && isContinued && (
          <div className="bfp-billlater">
            <div className="bfp-billlater-title">Job still going?</div>
            <p className="bfp-muted">
              This job continues on <b>WO {continuedToWO || "the next job"}</b>. Nothing is billed on
              WO {job?.wo || "this job"} now - the balance rolls over and is billed on the continuation job.
            </p>
            <button type="button" className="bfp-btn bfp-btn-billlater"
              disabled={saving}
              onClick={billLater}
              style={{ background: "#2563eb", backgroundImage: "none", borderColor: "#2563eb", color: "#fff" }}
            >
              {saving ? "Saving…" : `Bill Later – continues on WO ${continuedToWO || "next job"}`}
            </button>
          </div>
        )}
        {askNoPay && (
          <div className="bfp-nopay">
            <div className="bfp-nopay-title">Why is there no payment?</div>
            <p className="bfp-muted">
              Saves a $0.00 invoice marked <b>Paid</b>. Materials and labor already spent on the job stay on it as costs.
            </p>
            <div className="bfp-nopay-reasons">
              {NO_PAYMENT_REASONS.map((r) => (
                <button key={r} type="button" className="bfp-nopay-reason" disabled={saving} onClick={() => noPayment(r)}>
                  {saving ? "Saving…" : r}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="bfp-muted bfp-foot">
          Decide later leaves the job in "Completed jobs waiting for an invoice" on Approved Invoices. No Payment Needed
          is for proposals, meetings, inspections, warranty and other work we don't get paid for (techs are still paid).
          {isContinued ? " Bill Later (under No Payment Needed) is for a job that is still going on the next WO." : ""}
        </p>
      </div>
    </div>
  );
};

export default BillingTypePopup;
